/** The quick actions and "used by" links every value on a tag surface offers —
 *  shared by the tag page (header, General/Data tabs, Used by), the tag
 *  category page (header, General, Vocabulary, Used by) and their tests, so the
 *  same reference gets the same set everywhere.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *
 *  Id spaces (see gridLinks): a tag is pinned with `=#<id>` on /bookings,
 *  /orders and /tours (the plain form matches every same-NAMED tag), while a
 *  CATEGORY is matched by its `code` — `category=`, `order_category=`, … take
 *  the code, not the pk. The users grid is a plain-param grid whose `?tag=`
 *  accepts the tag id. `/notifications/features` has only an `icontains` `tag`
 *  namespace (it searches the stored chip text), so a tag is found there by
 *  NAME — which is also why that link can over-match and is labelled
 *  "mentioning".
 *
 *  Never: `/delivery-logs?q=tag=` (that is the redelivery LOG tag) or
 *  `/vehicles?edit=` (the vehicles page has no edit intent).
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  featureFlagsHref,
  findReplaceHref,
  idCond,
  ordersHref,
  tagsHref,
  toursHref,
  usersHref,
} from '@/utils/gridLinks';

export interface TagCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: TagCtx) => ctx.tenantId ?? null;

/** One row of a "Used by" section: a grid, pre-filtered by this record.
 *  Rendered as an inline link (same tab) rather than a popover action — the
 *  section IS the affordance, so there is nothing to hover. */
export interface UsedByLink {
  key: string;
  label: string;
  href: string;
  /** What the link actually asks the grid — shown as the row's caption. */
  hint: string;
}

// ---- Tag page -------------------------------------------------------------

/** The tag's category: open it, list its tags, filter by its code. */
export function tagCategoryActions(
  category: { id: number; code: string },
  ctx: TagCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/tag-categories/${category.id}`, 'Open category'),
    act.filter(tagsHref([], t, { category: category.id }), 'Tags in this category'),
    act.filter(
      bookingsHref([cond('category', category.code)], t),
      'Bookings with any tag in this category',
    ),
    act.copy(category.code, 'Copy category code'),
  ];
}

/** The user a tag is associated with (guide tags, vehicle-driver tags, …). */
export function tagUserActions(
  user: { id: number; email: string | null; name: string | null },
  ctx: TagCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.open(`/users/${user.id}`, 'Open user'),
    act.filter(bookingsHref([idCond('user', user.id)], t), 'Bookings of this user'),
    act.filter(tagsHref([idCond('user', user.id)], t), 'Tags of this user'),
  ];
  if (user.email) out.push(act.mailto(user.email), act.copy(user.email, 'Copy email'));
  return out;
}

/** The vehicle a tag is associated with. `/vehicles` has no detail route and
 *  no `?edit=` intent, so the only honest link is the tag list. */
export function tagVehicleActions(vehicle: { id: number }, ctx: TagCtx): ValueAction[] {
  return [
    act.filter(
      tagsHref([cond('vehicle', String(vehicle.id), 'exact')], tenantOf(ctx)),
      'Tags of this vehicle',
    ),
  ];
}

/** Where this tag is used. Counts need a payload the tag detail does not carry
 *  (no usage aggregates), so these are links, not numbers. */
export function tagUsedByLinks(tag: { id: number; name: string }, ctx: TagCtx): UsedByLink[] {
  const t = tenantOf(ctx);
  const pin = (relation: string) => idCond(relation, tag.id);
  return [
    {
      key: 'bookings',
      label: 'Bookings with this tag',
      href: bookingsHref([pin('tag')], t),
      hint: 'tag=#' + tag.id,
    },
    {
      key: 'bookings-order',
      label: 'Bookings whose ORDER has this tag',
      href: bookingsHref([pin('order_tag')], t),
      hint: 'order_tag=#' + tag.id,
    },
    {
      key: 'bookings-user',
      label: 'Bookings whose USER has this tag',
      href: bookingsHref([pin('user_tag')], t),
      hint: 'user_tag=#' + tag.id,
    },
    {
      key: 'bookings-tour',
      label: 'Bookings whose TOUR has this tag',
      href: bookingsHref([pin('tour_tag')], t),
      hint: 'tour_tag=#' + tag.id,
    },
    {
      key: 'orders',
      label: 'Orders with this tag',
      href: ordersHref([pin('order_tag')], t),
      hint: 'order_tag=#' + tag.id,
    },
    {
      key: 'tours',
      label: 'Tours with this tag',
      href: toursHref([pin('tag')], t),
      hint: 'tag=#' + tag.id,
    },
    {
      key: 'users-guests',
      label: 'Guest accounts with this tag',
      href: usersHref('guests', { tenant: t ?? undefined, tag: tag.id }),
      hint: 'tag=' + tag.id,
    },
    {
      key: 'users-staff',
      label: 'Staff accounts with this tag',
      href: usersHref('staff', { tenant: t ?? undefined, tag: tag.id }),
      hint: 'tag=' + tag.id,
    },
    {
      key: 'feature-flags',
      label: 'Feature flags mentioning this tag',
      // The flags grid indexes the chip TEXT, not the tag id — a same-named tag
      // in another category matches too, so the label says "mentioning".
      href: featureFlagsHref([cond('tag', tag.name, 'icontains')]),
      hint: 'tag=~' + tag.name,
    },
  ];
}

// ---- Tag category page ----------------------------------------------------

/** The category code — what every `category=` namespace matches on. */
export function categoryCodeActions(
  category: { id: number; code: string },
  ctx: TagCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(category.code, 'Copy code'),
    act.filter(tagsHref([], t, { category: category.id }), 'Tags in this category'),
    act.filter(
      bookingsHref([cond('category', category.code)], t),
      'Bookings with any tag in this category',
    ),
    act.filter(
      ordersHref([cond('order_category', category.code)], t),
      'Orders with any tag in this category',
    ),
  ];
}

/** The reusable schema a category references. */
export function categorySchemaActions(ref: { id: number; codename: string }): ValueAction[] {
  return [
    act.open(`/tag-schemas/${ref.id}/schema`, 'Open schema'),
    act.copy(ref.codename, 'Copy schema codename'),
  ];
}

/** An access group that gates this category. */
export function categoryAccessGroupActions(
  group: { id: number; name: string | null },
  ctx: TagCtx,
): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/rbac/access-groups/${group.id}`, 'Open access group'),
    act.filter(
      bookingsHref([idCond('agroup', group.id)], tenantOf(ctx)),
      'Bookings of this access group',
    ),
  ];
  if (group.name) out.push(act.copy(group.name, 'Copy group name'));
  return out;
}

/** Where a category's tags are used. Same reasoning as `tagUsedByLinks`: the
 *  payload carries one count (live tags), never per-grid usage. */
export function categoryUsedByLinks(
  category: { id: number; code: string },
  ctx: TagCtx,
): UsedByLink[] {
  const t = tenantOf(ctx);
  const byCode = (ns: string) => cond(ns, category.code);
  return [
    {
      key: 'tags',
      label: 'Tags in this category',
      href: tagsHref([], t, { category: category.id }),
      hint: 'category=' + category.id,
    },
    {
      key: 'bookings',
      label: 'Bookings with any tag in this category',
      href: bookingsHref([byCode('category')], t),
      hint: 'category=' + category.code,
    },
    {
      key: 'bookings-user',
      label: 'Bookings whose USER has a tag in this category',
      href: bookingsHref([byCode('user_category')], t),
      hint: 'user_category=' + category.code,
    },
    {
      key: 'bookings-tour',
      label: 'Bookings whose TOUR has a tag in this category',
      href: bookingsHref([byCode('tour_category')], t),
      hint: 'tour_category=' + category.code,
    },
    {
      key: 'orders',
      label: 'Orders with any tag in this category',
      href: ordersHref([byCode('order_category')], t),
      hint: 'order_category=' + category.code,
    },
  ];
}

/** A vocabulary property name: copy it, or find every record that stores it.
 *  `tenantCode` is the operator's code — `system` for a system category, which
 *  is the spelling the find-replace `tenant` namespace expects. */
export function vocabularyPropertyActions(name: string, tenantCode: string): ValueAction[] {
  return [
    act.copy(name, 'Copy attribute name'),
    act.filter(
      findReplaceHref([cond('attr', name), cond('tenant', tenantCode)]),
      'Records storing this attribute',
    ),
  ];
}
